import React, { PropsWithChildren, useEffect, useRef } from 'react';
import styles from './popup.module.scss';
import { Button, Tooltip } from '@americanexpress/dls-react';
import CloseIcon from 'src/assets/svg/popup/close-button.svg';
import Anchor from '../../atoms/anchor/Anchor';
import Typography from 'src/components/typography/Typography';
import { TypographyVariants } from 'src/types/typography-variants.enum';
import { PopupPlacement } from 'src/types/popup.model';
import Selectors from 'src/utils/selectors';
import { popupLeftTop, popupPlacementToFlexDirectionClass, popupPlacementToOffsetShift } from 'src/utils/popup.utils';

export interface PopupProps {
    id: string;
    content: string;
    nextButtonText: string;
    progressText: string;
    placement: PopupPlacement;
    handleClose: () => Promise<void>;
    handleNext: () => Promise<void>;
    leftOverride?: string;
    topOverride?: string;
    scrollIntoView?: boolean;
    className?: string;
    closeButtonAriaLabel?: string;
    nextButtonAriaLabel?: string;
}

export default function Popup({
    id,
    content,
    nextButtonText,
    progressText,
    placement,
    handleClose,
    handleNext,
    leftOverride = null,
    topOverride = null,
    scrollIntoView = true,
    className = '',
    closeButtonAriaLabel,
    nextButtonAriaLabel,
    children
}: PropsWithChildren<PopupProps>): JSX.Element {
    const flexDirectionClass: string = popupPlacementToFlexDirectionClass(placement);
    const offset: popupLeftTop = popupPlacementToOffsetShift(placement);
    const focusRef = useRef(null);
    if (leftOverride) {
        offset.left = leftOverride;
    }
    if (topOverride) {
        offset.top = topOverride;
    }
    useEffect(() => {
        if (scrollIntoView) {
            setTimeout(() => {
                const element = document.getElementById(id);
                const rect = element?.getBoundingClientRect();
                const viewHeight = Math.max(document?.documentElement?.clientHeight, window?.innerHeight);
                const visible =
                    rect?.top > 0 && rect?.top < viewHeight && rect?.bottom > 0 && rect?.bottom < viewHeight;
                if (element && rect && !visible) {
                    element.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
                }
            }, 100);
        }
    }, []);

    useEffect(() => {
        focusRef?.current?.focus();
    }, [focusRef?.current]);

    return (
        <div className={`flex ${flexDirectionClass}`} data-cy={Selectors.SpendSmart.POPUP}>
            {children}
            <Tooltip
                id={id}
                triggerElement={<span />}
                open={true}
                placement={placement}
                className={`${styles.popupContainer} ${className}`}
                data-cy={Selectors.SpendSmart.POPUP_TOOLTIP}
                style={{ left: offset.left, top: offset.top }}
                aria-expanded={null}
            >
                <div className={`body-1 ${styles.innerContainer}`} tabIndex={0} ref={focusRef}>
                    <div
                        className={styles.textContent}
                        id="popup-content"
                        aria-label={`Tooltip step ${progressText}: ${content}`}
                    >
                        {content}
                    </div>
                    <Button
                        onClick={handleClose}
                        className={styles.popupCloseButton}
                        data-cy={Selectors.SpendSmart.CLOSE_BUTTON}
                        aria-label={closeButtonAriaLabel ?? 'Close'}
                    >
                        <img src={CloseIcon} className={styles.closeIcon} alt="Close button" />
                    </Button>
                    <div className={styles.bottomContent}>
                        <div>
                            <Anchor
                                onClick={handleNext}
                                className={styles.nextAnchor}
                                data-cy={Selectors.SpendSmart.NEXT_BUTTON}
                                aria-label={nextButtonAriaLabel ?? nextButtonText}
                            >
                                {nextButtonText}
                            </Anchor>
                        </div>
                        <Typography
                            variant={TypographyVariants.LEGAL_SECONDARY}
                            className={styles.progressText}
                            data-cy={Selectors.SpendSmart.POPUP_PROGRESS_TEXT}
                        >
                            {progressText}
                        </Typography>
                    </div>
                </div>
            </Tooltip>
        </div>
    );
}
